import { Grid } from '@material-ui/core'
import { useRouter } from 'next/router'
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { batch } from 'react-redux'
import { client } from '../../../apollo/client'
import { CardDesignItem } from '../../../components/CardDesignCarousel/CardDesignItem'
import { LoadMoreButton } from '../../../components/searchresultcard/LoadMoreButton'
import { TopicFilters } from '../../../components/topicfilters/TopicFilters'
import { isNotNull, nonNull } from '../../../utils/nonNull'
import { truncate } from '../../../utils/truncate'
import { NewsLandingQuery, NewsLandingQueryVariables } from '../__generated__'
import { NEWS_LANDING_QUERY } from '../index'
import {
  StyledArticleWrapper,
  StyledButtonGrid,
  StyledCardWrapper,
  StyledFiltersGrid,
  StyledNewsContainer,
  StyledNewsItem,
  StyledNewsTitle,
  StyledSectionWrapper,
} from '../templateStyles'

export type NewsLandingProps = NewsLandingQuery & {
    //
}

export const NewsFilters: React.FC<PropsWithChildren<NewsLandingProps>> = ({ entry, globalSet, categories, entries: baseEntries, entryCount: baseEntryCount }) => {
  if (!entry || entry.__typename !== 'newsLandingPage_newsLandingPage_Entry') {
    return null
  }

  const router = useRouter()
  const [entryCount, setEntryCount] = useState(baseEntryCount)
  const [entries, setEntries] = useState(isNotNull(baseEntries) ? baseEntries : [])
  const categoryParam = router.query.category as string || null
  const [selectedNewsFilter, setSelectedNewsFilter] = useState<string>('001')
  const [loading, setLoading] = useState(false)
  const [offset, setOffset] = useState(0)

  const newsFilters = categories ? (nonNull(categories).map(category => {
    if (category && category.__typename === 'newsArticles_Category') {
      return {
        value: category.id,
        text: category.title,
      }
    }
  }) as ({ value: string; text: string })[]).sort((a,b) => (a.text > b.text) ? 1 : ((b.text > a.text) ? -1 : 0)) : []

  newsFilters.unshift({ value: '001', text: 'Show all' })

  let imageFallbackUrl: string | undefined

  if(isNotNull(globalSet) && globalSet.__typename === 'siteSettings_GlobalSet' && isNotNull(globalSet.newsFallbackImage) && isNotNull(globalSet.newsFallbackImage[0]))
  {
    imageFallbackUrl = globalSet.newsFallbackImage?.[0]?.url??''
  }

  const articles = entries ? entries.map((item) => {
    if (item && item.__typename === 'news_newsEntryType_Entry') {      
      return (
        {
          id: item.id,
          title: item.title,
          link: `/${item.uri}`,
          imgUrl: item.newsDetailBanner?.[0]?.bannerImage?.[0]?.url??imageFallbackUrl,
          date: item.postDate,
          categoryID: item.newsCategory?.filter(isNotNull).map(category => category.id),
        }
      )
    }

    return null
  }).filter(isNotNull) : []

  const loadMore = useCallback( async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setLoading(true)

    const { data } = await client.query<NewsLandingQuery, NewsLandingQueryVariables>({
      query: NEWS_LANDING_QUERY,
      variables: {
        uri: window.location.pathname,
        offset: offset + 12,
        category: selectedNewsFilter === '001' ? null : [selectedNewsFilter],
      },
    })

    batch(() => {
      setOffset(offset + 12)
      setLoading(false)
      setEntryCount(data.entryCount)

      if (isNotNull(data.entries)) {
        setEntries([...entries, ...nonNull(data.entries)])
      }
    })
  },[entries, offset, selectedNewsFilter])

  const onChange = useCallback((values) => {
    if (values !== null && values.length > 0) {
      setSelectedNewsFilter(values[0])
    } else {
      setSelectedNewsFilter('001')
    }
  }, [])

  useEffect(() => {
    // set loading fade out/in in 600 ms
    setLoading(true)
    setTimeout(() => { setLoading(false) }, 600)

    client.query<NewsLandingQuery, NewsLandingQueryVariables>({
      query: NEWS_LANDING_QUERY,
      variables: {
        uri: window.location.pathname,
        offset: 0,
        category: selectedNewsFilter === '001' ? null : [selectedNewsFilter],
      },
    }).then(({ data }) =>
      batch(() => {
        setOffset(0)
        setEntries(isNotNull(data.entries) ? nonNull(data.entries) : [])
        setEntryCount(data.entryCount)

        // Anchor down to the article filter section and listing
        setTimeout(() => {
          if (categoryParam && typeof window === 'object') {
            const topicFilterSection: unknown = document.querySelector('h5[class*="topic-filter-title"]')

            if (topicFilterSection) {
              (topicFilterSection as Element).scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              })
            }
          }
        } , 200)
      }),
    )
  }, [categoryParam, selectedNewsFilter])

  useEffect(()=>{
    if(location.search && location.search != undefined){
      setSelectedNewsFilter(location.search.split('=')[1] as string)
    }
  },[])

  return (
    <>
      <StyledSectionWrapper direction={'column'} container>
        <StyledFiltersGrid item xs md={12}>
          <TopicFilters
            multiple={false}
            filterOptions={newsFilters}
            selectedFilters={[selectedNewsFilter]}
            filterTitle={`Filter by - ${newsFilters.find(x => x.value === selectedNewsFilter)?.text ?? 'All'}`}
            onChangeEvent={onChange}
            loading={loading}
          />
        </StyledFiltersGrid>
        <StyledNewsContainer item xs>
          <StyledNewsTitle align="center" variant="h2">{'Latest News'}</StyledNewsTitle>
          <StyledArticleWrapper $loading={loading}>
            <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={8}>
              {articles.map(( item ) => {
                return (
                  <StyledNewsItem key={item.id} item xs={12} sm={6} md={4}>
                    <StyledCardWrapper key={item.id} href={item.link}>
                      <CardDesignItem image src={item.imgUrl} alt={''} layout="fill" objectFit="cover" objectPosition="center" />
                      <CardDesignItem date>{new Date(item.date).toLocaleString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })}</CardDesignItem>
                      <CardDesignItem title>{truncate(item.title ?? 'Untitled', 75)}</CardDesignItem>
                    </StyledCardWrapper>
                  </StyledNewsItem>
                )
              })}
            </Grid>
          </StyledArticleWrapper>
        </StyledNewsContainer>
      </StyledSectionWrapper>
      <StyledButtonGrid item alignItems={'center'} container>
        <LoadMoreButton
          primary
          buttonColor="black"
          onClick={loadMore}
          isLoading={loading}
          loadedItems={entries.length}
          totalNumber={entryCount ? entryCount : entries.length}>
          {'Load more'}
        </LoadMoreButton>
      </StyledButtonGrid>
    </>
  )
}